import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { createSelector } from "reselect";
import Slider from "react-slick";

import { getImageUrl } from "../../../lib/url";

import "./ProductImageGallery.scss";
import Modal from "../../../component/Modal";
import Image from "../../../component/Image";

const getCloudZoomImage = img => img.set("zc", 1).set("fileType", "jpg");

const getLargeUrl = img =>
  getImageUrl(
    getCloudZoomImage(img)
      .set("minWidth", 350)
      .toJS()
  );
const getSmallUrl = img =>
  getImageUrl(
    getCloudZoomImage(img)
      .set("minWidth", 350)
      .set("maxSize", 350)
      .toJS()
  );
const getThumbnailUrl = img =>
  getImageUrl(
    getCloudZoomImage(img)
      .set("maxSize", 74)
      .toJS()
  );

const NextArrow = props => (
  <span {...props}>
    <i className="fa fa-fw fa-chevron-right" />
  </span>
);

const PrevArrow = props => (
  <span {...props}>
    <i className="fa fa-fw fa-chevron-left" />
  </span>
);

// const injectPreloadMetaTag = (href, as) => {
//   const el = document.createElement("meta");
//   el.setAttribute("name", "preload");
//   el.setAttribute("href", href);
//   el.setAttribute("as", as);
//   document.getElementsByTagName("body")[0].appendChild(el);
// };

const settings = {
  arrows: true,
  dots: true,
  initialSlide: 0,
  slidesToShow: 1,
  slidesToScroll: 1,
  speed: 500,
  touchMove: true,
  touchThreshold: 5,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />
};

/**
 * ProductImageGallery / CloudZoom
 */
class ProductImageGallery extends Component {
  static propTypes = {
    productTitle: PropTypes.string,
    className: PropTypes.string,
    // eslint-disable-next-line react/require-default-props,react/forbid-prop-types
    style: PropTypes.object,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        displayUrl: PropTypes.string,
        thumbnailUrl: PropTypes.string,
        title: PropTypes.string
      })
    )
  };

  static defaultProps = {
    style: null,
    productTitle: "",
    className: "",
    images: []
  };

  constructor(props) {
    super(props);

    this.onPreventTouch = this.onPreventTouch.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);

    this.galleryRef = React.createRef();
  }

  state = {
    isModalOpen: false
  };

  componentDidMount() {
    // this.props.images.forEach(image => {
    //   injectPreloadMetaTag(image.url, "image");
    // });

    // Disable touchmove to prevent scrolling entire page
    const carousel = this.galleryRef.current;
    if (!carousel) {
      return;
    }
    // document.getElementById("someid"); // Your site element containing react-slick's carousel-container
    carousel.addEventListener("touchstart", this.onTouchStart);
    carousel.addEventListener("touchmove", this.onPreventTouch, {
      passive: false
    });
  }

  onTouchStart(e) {
    // capture user's starting finger position, for later comparison
    this.firstClientX = e.touches[0].clientX;
  }

  onPreventTouch(e) {
    // only prevent touch on horizontal scroll (for horizontal carousel)
    // this allows the users to scroll vertically past the carousel when touching the carousel
    // this also stabilizes the horizontal scroll somewhat, decreasing vertical scroll while horizontal scrolling
    const clientX = e.touches[0].clientX - this.firstClientX;
    const horizontalScroll = Math.abs(clientX) > settings.touchThreshold;
    if (horizontalScroll) {
      e.preventDefault();
    }
  }

  onClickPeek = () => {
    const scrollBarGap = window.innerWidth - document.documentElement.clientWidth;
    let previousBodyPaddingRight;
    if (scrollBarGap > 0) {
      previousBodyPaddingRight = document.body.style.paddingRight || "0px";
      document.body.style.paddingRight = `${scrollBarGap}px`;
      document.getElementById("header").style.paddingRight = document.body.style.paddingRight;
    }
    this.setState({
      previousBodyPaddingRight,
      isModalOpen: true
    });
  };

  onCancelPeek = () => {
    if (this.state.previousBodyPaddingRight) {
      document.body.style.paddingRight = this.state.previousBodyPaddingRight;
      document.getElementById("header").style.paddingRight = document.body.style.paddingRight;
    }
    this.setState({
      isModalOpen: false
    });
  };

  render() {
    const { images } = this.props;
    if (images.length === 0) {
      return null; // no images for you my friend
    }

    const modalBoxSize = window.innerWidth <= 500 ? window.innerWidth - 40 : "560px";

    return (
      <div className="ProductImageGallery" style={this.props.style} ref={this.galleryRef}>
        <Slider {...settings}>
          {this.props.images.map(image => (
            <div className="ProductImageGallery__Slide" key={image.url}>
              <img
                intrinsicsize="350 x 350"
                src={image.displayUrl || image.thumbnailUrl || image.url}
                alt={image.title}
              />
            </div>
          ))}
        </Slider>
        <div
          className="ProductImageGallery__Peek"
          role="button"
          tabIndex="-1"
          onClick={this.onClickPeek}
          onKeyPress={this.onClickPeek}
        >
          <i className="fa fa-fw fa-search" />
        </div>
        <Modal
          className="ProductImageGallery__PeekModal"
          title={this.props.productTitle}
          isOpen={this.state.isModalOpen}
          onCancel={this.onCancelPeek}
          customStyles={{
            content: {
              minHeight: modalBoxSize,
              width: modalBoxSize
            }
          }}
        >
          <Slider {...settings}>
            {this.props.images.map(image => (
              <div className="ProductImageGallery__Slide" key={image.url}>
                <Image src={image.url} alt={image.title} />
              </div>
            ))}
          </Slider>
        </Modal>
      </div>
    );
  }
}

const selectContext = createSelector(
  [state => state.cart],
  cart => cart.get("context")
);

const selectContextImages = createSelector(
  [state => selectContext(state)],
  ctx => ctx.getIn(["webProduct", "images"])
);

const selectImages = createSelector(
  [state => selectContextImages(state)],
  images => {
    if (!images) {
      return [];
    }
    return images
      .map(i => ({
        title: "",
        url: getLargeUrl(i),
        displayUrl: getSmallUrl(i),
        thumbnailUrl: getThumbnailUrl(i)
      }))
      .toJS();
  }
);

export default connect(state => ({
  images: selectImages(state),
  productTitle: state.cart.getIn(["context", "webProduct", "title"], "")
}))(ProductImageGallery);
